import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRowHeight } from '../utils/useRowHeight';
import { api } from '../services/api';
import CycleBar from '../components/CycleBar';
import ValidationAlert from '../components/ValidationAlert';
import AddProductModal from '../components/AddProductModal';
// Tải lười — kéo theo thư viện xlsx (~290KB) chỉ để đọc file Excel, đa số
// người dùng không bấm "Nhập từ file" mỗi lần vào trang này.
const ImportForecastModal = React.lazy(() => import('../components/ImportForecastModal'));
import { Save, Send, Search, Loader2, Wand2, ArrowDownToLine, PackagePlus, FileSpreadsheet, ListChecks, Undo2, RotateCcw } from 'lucide-react';
import { monthsOfCycle, weeksOfMonth, weekLabel, monthLabel, monthLabelFull, normalizeMonth } from '../utils/period';
import { setDirty } from '../services/dirtyState';
import { appConfirm } from '../services/dialogService';
import { thongBao } from '../services/toastService';
import { loadPref, savePref } from '../services/prefs';
import { usePersistedState } from '../utils/usePersistedState';
import { useTableSort } from '../utils/useTableSort';
import { SortTh, StateRow } from '../components/TableStates';
import MoreMenu from '../components/MoreMenu';
import { useGridEditing, parsePastedNumber, CHAN_LAN_CHUOT } from '../utils/useGridEditing';
import { demGhiDe, cauHoiGhiDe } from '../utils/gridOverwrite';
import { datKenhChungChoCacMa, duocDatKenhChung } from '../utils/kenhChung';
import { capNhatRaiDeu, laDongLech, tapSkuLech, tinhRaiDeuTatCa, tuanDonHopLe, mienDonHopLe, lapBuocHoanTac, apHoanTac, skuNgoaiDanhMuc, oAnCuaBang } from '../utils/weeklyDistribute';
import { MAN_HINH } from '../utils/glossary';

// Bảng này nặng nhất trong app — kênh XK 756 SKU × (số tuần × số miền)
// ô input, có thể tới ~6000 ô nếu render hết cùng lúc. Chỉ dựng DOM cho
// dòng đang lọt khung nhìn.
const ROW_HEIGHT_PX = 37;

// Thông báo kết quả đi qua toast xếp hàng (Đợt 2 mục 2): thành công tự tắt ~4 giây, lỗi nằm lại tới khi bấm ×.
const setMessage = thongBao;
const laChuoi = (v) => typeof v === 'string';
const maSku = (p) => p.sku_code;
// Nút chính (xanh) và nút phụ (trắng viền): mỗi màn chỉ MỘT nút chính (Đợt 2 mục 4).
const NUT_CHINH = 'flex items-center space-x-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-2 rounded-lg text-xs font-semibold shadow transition disabled:opacity-50';
const NUT_PHU = 'flex items-center space-x-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3.5 py-2 rounded-lg text-xs font-semibold shadow-sm transition disabled:opacity-50';

export default function WeeklyForecast({ currentBU, user }) {
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [products, setProducts] = useState([]);
  // SKU có số nhưng không nằm trong danh mục đang bật của đơn vị + ô có số ở tuần/miền không hiện trên bảng (xem utils/weeklyDistribute.js).
  // Server vẫn cộng chúng vào kiểm tra khớp số, nên phải HIỆN ra để sửa được — trước đây bị ẩn nên cảnh báo lệch "không tìm thấy ở đâu".
  const [skuNgoai, setSkuNgoai] = useState([]);
  const [oAn, setOAn] = useState([]);
  const [regions, setRegions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [bus, setBus] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [monthlyMap, setMonthlyMap] = useState({});
  const [weeklyMap, setWeeklyMap] = useState({});
  const [dirtyKeys, setDirtyKeys] = useState(() => new Set());
  // Số đã lưu / vừa tải (mốc để Hoàn tác biết ô nào đã trở về như server) + các bước hoàn tác (mỗi lần dán / điền / phân bổ là một bước).
  const daLuuRef = useRef({});
  const [lichSu, setLichSu] = useState([]);

  // Chặn đổi tab/đổi đơn vị làm mất ô chưa lưu mà không hỏi lại
  useEffect(() => {
    setDirty(dirtyKeys.size > 0, `Bảng Forecast tuần/miền còn ${dirtyKeys.size} ô chưa lưu.`);
    return () => setDirty(false);
  }, [dirtyKeys]);
  const [validationResult, setValidationResult] = useState(null);
  const [search, setSearch] = useState('');
  // Nhớ ô "chỉ hiện SKU có số lượng" giữa các lần mở (Đợt 2 mục 9).
  const [onlyNonZero, setOnlyNonZero] = usePersistedState('weeklyOnlyNonZero', true, (v) => typeof v === 'boolean');
  const [nonZeroSkus, setNonZeroSkus] = useState(() => new Set());
  // Lọc "chỉ dòng lệch" (tổng tuần/miền khác FC tháng 1). Danh sách lệch được CHỐT lúc tải / lúc bật lọc, không tính lại theo từng phím gõ —
  // cùng lý do với nonZeroSkus: sửa cho một dòng hết lệch mà nó biến mất ngay dưới con trỏ thì không gõ tiếp được.
  const [chiLech, setChiLech] = useState(false);
  const [lechSkus, setLechSkus] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loiTai, setLoiTai] = useState(null);

  const baseMonth = normalizeMonth(selectedCycle?.base_month) || monthsOfCycle(selectedCycle)[0] || '';
  // Memo hoá để mảng giữ nguyên định danh giữa các lần render, nếu không mọi
  // useMemo phụ thuộc vào chúng đều bị tính lại sau từng phím gõ.
  const weeks = useMemo(() => (baseMonth ? weeksOfMonth(baseMonth) : []), [baseMonth]);
  const regionCodes = useMemo(() => regions.map((r) => r.code), [regions]);
  // Cách phân bổ số tháng 1 vào bảng tuần khi soạn tay (không nhập từ file): 'deu' = rải đều các tuần × miền, 'w3' = dồn TOÀN BỘ vào tuần 3.
  // Nhớ giữa các lần mở; chu kỳ khác không có tuần đó thì tự coi như rải đều (không báo lỗi, không đổi lựa chọn đã nhớ).
  const [cheDoRai, setCheDoRai] = usePersistedState('weeklyCheDoRai', 'deu', (v) => laChuoi(v) && /^(deu|w\d{1,2})$/.test(v));
  const tuanDon = tuanDonHopLe(cheDoRai.startsWith('w') ? cheDoRai.slice(1) : undefined, weeks);
  // Dồn vào một tuần = dồn vào MỘT miền (mặc định MB), không tự chia cho các miền (user chốt 25/10/2026).
  const [mienDonChon, setMienDonChon] = usePersistedState('weeklyMienDon', 'MB', laChuoi);
  const mienDon = regionCodes.length ? mienDonHopLe(mienDonChon, regionCodes) : undefined;
  const tenDon = tuanDon === undefined ? '' : `Tuần ${tuanDon} · ${mienDon}`;

  /** Tập SKU có số ở bảng tháng HOẶC ở bất kỳ ô tuần/miền nào. */
  const computeNonZero = useCallback((mMap, wMap) => {
    const s = new Set();
    Object.keys(mMap).forEach((sku) => { if ((mMap[sku] || 0) > 0) s.add(sku); });
    Object.keys(wMap).forEach((k) => {
      if (!(wMap[k] || 0)) return;
      // Khoá dạng `${sku}_${tuần}_${miền}` — bỏ đúng hai hậu tố cuối, vì
      // bản thân mã SKU có thể chứa dấu gạch dưới.
      const lastUnd = k.lastIndexOf('_');
      const secondLast = k.lastIndexOf('_', lastUnd - 1);
      if (secondLast > 0) s.add(k.slice(0, secondLast));
    });
    return s;
  }, []);

  const isEditor = user?.role === 'bu_editor' || user?.role === 'central_admin';
  // Rút lại phê duyệt là quyền của người thẩm định, không phải người lập kế hoạch
  const canReopen = user?.role === 'bu_approver' || user?.role === 'central_admin';
  const cycleLocked = selectedCycle?.status === 'approved' || selectedCycle?.status === 'locked';
  const canWrite = isEditor && !!selectedVersion && !cycleLocked;
  // Đang chờ duyệt: lưu là server rút yêu cầu duyệt (xem MonthlyForecast).
  const choDuyet = selectedCycle?.status === 'submitted';

  /** Áp dữ liệu đã có sẵn (từ action gộp) vào state, không gọi mạng thêm. */
  // Danh sách SKU / tuần / miền hiện hành, cho phần chốt danh sách "lệch" khi dữ liệu vừa tải (state chưa kịp cập nhật);
  // loadAll truyền thẳng giá trị mới vào `nho` vì lúc đó products/regions vừa được set trong cùng lượt.
  const ctxRef = useRef({ products: [], weeks: [], regionCodes: [] });
  useEffect(() => { ctxRef.current = { products, weeks, regionCodes }; }, [products, weeks, regionCodes]);

  const applyForecasts = useCallback((monthlyQuantities, splits, valRes, nho) => {
    const mMap = monthlyQuantities || {};
    setMonthlyMap(mMap);

    const wMap = {};
    (splits || []).forEach((s) => {
      wMap[`${s.sku_code}_${s.week_number}_${s.region_code}`] = Number(s.quantity) || 0;
    });
    setWeeklyMap(wMap);
    daLuuRef.current = { ...wMap };
    setLichSu([]);
    setNonZeroSkus(computeNonZero(mMap, wMap));
    const c = nho || ctxRef.current;
    const tenMa = {};
    (splits || []).forEach((sp) => { tenMa[String(sp.sku_code).trim()] = sp.product_name; });
    ((valRes && valRes.mismatches) || []).forEach((m) => { if (!tenMa[String(m.sku_code).trim()]) tenMa[String(m.sku_code).trim()] = m.product_name; });
    const ngoai = skuNgoaiDanhMuc(c.products, mMap, wMap, tenMa);
    setSkuNgoai(ngoai);
    setOAn(oAnCuaBang(wMap, c.weeks, c.regionCodes));
    setLechSkus(tapSkuLech(c.products.concat(ngoai), mMap, wMap, c.weeks, c.regionCodes));
    setValidationResult(valRes || null);
    setDirtyKeys(new Set());
  }, [computeNonZero]);

  const loadForecasts = useCallback(async (versionId, month) => {
    const [mLines, wSplits, valRes] = await Promise.all([
      api.getMonthlyLines(versionId),
      api.getWeeklySplits(versionId),
      api.validateWeeklySplits(versionId)
    ]);

    const mMap = {};
    mLines.forEach((l) => {
      if (normalizeMonth(l.forecast_month) === month) {
        mMap[l.sku_code] = Number(l.quantity) || 0;
      }
    });
    applyForecasts(mMap, wSplits, valRes);
  }, [applyForecasts]);

  const loadVersions = useCallback(async (cycle, preferVersionId) => {
    const list = await api.getCycleVersions(cycle.id);
    setVersions(list);
    const chosen =
      list.find((v) => v.id === preferVersionId) ||
      list.find((v) => String(v.is_final) === '1') ||
      list[list.length - 1] ||
      null;
    setSelectedVersion(chosen);
    if (chosen) await loadForecasts(chosen.id, normalizeMonth(cycle.base_month));
    else { setWeeklyMap({}); setMonthlyMap({}); setValidationResult(null); }
  }, [loadForecasts]);

  /**
   * Một lượt gọi thay cho chuỗi getCycles -> getVersions -> (getMonthlyLines
   * + getWeeklySplits + validateWeekly): 3 chặng nối tiếp, tổng 5 lượt gọi
   * mạng. Phần kiểm tra khớp số được backend tính ngay từ dữ liệu vừa đọc.
   * getRegions/getGroups/getBUs lấy từ bootstrap đã cache, không tốn lượt gọi.
   */
  const loadAll = useCallback(async (preferCycleId, preferVersionId) => {
    setLoading(true);
    setLoiTai(null);
    try {
      const [ws, regionList, groupList, buList] = await Promise.all([
        api.getWeeklyWorkspace({ bu: currentBU, cycleId: preferCycleId, versionId: preferVersionId }),
        api.getRegions(),
        api.getGroups(),
        api.getBUs()
      ]);

      setCycles(ws.cycles || []);
      setProducts(ws.products || []);
      setRegions(ws.regions?.length ? ws.regions : regionList);
      setGroups(groupList);
      setBus(buList);

      setSelectedCycle(ws.cycle || null);
      setVersions(ws.versions || []);
      setSelectedVersion(ws.version || null);
      const regionsMoi = ws.regions?.length ? ws.regions : regionList;
      const thangGoc = normalizeMonth(ws.cycle?.base_month) || monthsOfCycle(ws.cycle)[0] || '';
      applyForecasts(ws.monthlyQuantities, ws.splits, ws.validation, {
        products: ws.products || [], weeks: thangGoc ? weeksOfMonth(thangGoc) : [], regionCodes: regionsMoi.map((r) => r.code)
      });
      // Nhớ chu kỳ đang xem (theo đơn vị; dùng chung với Bảng 0) — Đợt 2 mục 9.
      if (ws.cycle?.id) savePref('cycle:' + currentBU, ws.cycle.id);
    } catch (err) {
      // Lỗi tải: bảng hiện lỗi + "Thử lại" (không im lặng thành bảng rỗng).
      setLoiTai(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentBU, applyForecasts]);

  // Lần mở đầu: vào lại đúng chu kỳ lần trước (server rơi về chu kỳ mới nhất nếu mã đó không còn).
  useEffect(() => {
    if (currentBU) loadAll(loadPref('cycle:' + currentBU, undefined, laChuoi));
  }, [currentBU, loadAll]);

  const handleSelectCycle = async (cycle) => {
    if (!cycle) return;
    savePref('cycle:' + currentBU, cycle.id);
    setSelectedCycle(cycle);
    setLoading(true);
    try {
      await loadVersions(cycle);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVersion = async (version) => {
    if (!version || !selectedCycle) return;
    setSelectedVersion(version);
    setLoading(true);
    try {
      await loadForecasts(version.id, normalizeMonth(selectedCycle.base_month));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const setCell = (key, qty) => {
    setWeeklyMap((prev) => ({ ...prev, [key]: qty }));
    setDirtyKeys((prev) => new Set(prev).add(key));
  };

  const handleCellChange = (skuCode, week, region, value) => {
    const parsed = value === '' ? 0 : Number(value);
    setCell(`${skuCode}_${week}_${region}`, Number.isFinite(parsed) && parsed >= 0 ? parsed : 0);
  };

  /** Áp nhiều ô cùng lúc (dán khối từ Excel, fill-down, Ctrl+D) trong 1 lần cập nhật. */
  const handleCellsChange = (updates) => {
    const buoc = lapBuocHoanTac(updates.map((u) => ({ ...u, value: parsePastedNumber(u.value) })), weeklyMap);
    if (buoc) setLichSu((h) => [...h.slice(-29), buoc]);
    setWeeklyMap((prev) => {
      const next = { ...prev };
      updates.forEach(({ rowKey, col, value }) => {
        next[`${rowKey}_${col.week}_${col.region}`] = parsePastedNumber(value);
      });
      return next;
    });
    setDirtyKeys((prev) => {
      const next = new Set(prev);
      updates.forEach(({ rowKey, col }) => next.add(`${rowKey}_${col.week}_${col.region}`));
      return next;
    });
  };

  /** Hoàn tác bước gần nhất (dán / điền / phân bổ): trả các ô về số trước đó. Gõ tay từng ô không thành bước riêng — dùng "Bỏ thay đổi chưa lưu". */
  const hoanTac = () => {
    const buoc = lichSu[lichSu.length - 1];
    if (!buoc) return;
    const kq = apHoanTac(buoc, weeklyMap, dirtyKeys, daLuuRef.current);
    setWeeklyMap(kq.weeklyMap);
    setDirtyKeys(kq.dirty);
    setOAn(oAnCuaBang(kq.weeklyMap, weeks, regionCodes));   // hoàn tác "Xoá số ở ô không hiển thị" thì báo ô ẩn trở lại
    setLichSu((h) => h.slice(0, -1));
    setMessage({ type: 'success', text: `Đã hoàn tác bước gần nhất (${buoc.length.toLocaleString('vi-VN')} ô).` });
  };

  /** Bỏ MỌI thay đổi chưa lưu: bảng về đúng số đã lưu / vừa tải. */
  const boThayDoi = async () => {
    if (!dirtyKeys.size) return;
    if (!(await appConfirm(
      `Bỏ ${dirtyKeys.size.toLocaleString('vi-VN')} ô đang sửa chưa lưu và quay về số đã lưu gần nhất? Không hoàn lại được.`,
      { title: 'Bỏ thay đổi chưa lưu?', okLabel: 'Bỏ thay đổi', danger: true }
    ))) return;
    setWeeklyMap({ ...daLuuRef.current });
    setDirtyKeys(new Set());
    setOAn(oAnCuaBang(daLuuRef.current, weeks, regionCodes));
    setLichSu([]);
    setMessage({ type: 'success', text: 'Đã bỏ các thay đổi chưa lưu.' });
  };

  /** Phân bổ số tháng 1 của một SKU ra các ô tuần × miền theo cách đang chọn: rải đều (phần dư dồn ô cuối) hoặc dồn hết vào MỘT ô tuần × miền (tính ở utils/weeklyDistribute.js). */
  const distributeEvenly = (skuCode) => {
    const updates = capNhatRaiDeu(skuCode, monthlyMap[skuCode] || 0, weeks, regionCodes, tuanDon, mienDon);
    if (updates.length) handleCellsChange(updates);
  };

  /**
   * "Rải đều tất cả dòng lệch": cùng cách tính với nút rải đều từng dòng, áp cho mọi SKU đang lệch trong danh sách đang hiển thị.
   * Có xem trước (bao nhiêu dòng, bao nhiêu ô đang có số sẽ bị thay) và hỏi xác nhận; chưa lưu cho tới khi bấm "Lưu bản thảo".
   */
  const raiDeuTatCaLech = async (tatCa = false) => {
    const kq = tinhRaiDeuTatCa({ danhSach: filteredProducts, monthlyMap, weeklyMap, tuan: weeks, mien: regionCodes, tuanDon, mienDon, tatCa });
    const doiTuong = tatCa ? 'SKU có số tháng 1' : 'SKU đang lệch';
    if (!kq.dong) {
      setMessage({ type: 'error', text: kq.boQua
        ? `Không có dòng nào phân bổ được: ${kq.boQua} SKU lệch nhưng số tháng 1 bằng 0 (phân bổ chỉ là xoá số tuần/miền — hãy tự kiểm tra các dòng đó).`
        : (tatCa ? 'Không có dòng nào có số tháng 1 để phân bổ.' : 'Không có dòng nào đang lệch.') });
      return;
    }
    const phamVi = search.trim() !== '' ? ' trong các dòng khớp ô tìm kiếm' : (onlyNonZero ? ' trong các dòng đang hiển thị' : '');
    const cachChia = tuanDon === undefined
      ? `Rải đều số tháng 1 ra ${weeks.length} tuần × ${regionCodes.length} miền cho ${kq.dong.toLocaleString('vi-VN')} ${doiTuong}${phamVi}.\n\n`
        + `Số tuần/miền đang nhập của các SKU này sẽ bị thay bằng số chia đều${kq.oGhiDe ? ` (${kq.oGhiDe.toLocaleString('vi-VN')} ô đang có số sẽ đổi)` : ''}; phần dư dồn vào ô cuối. `
      : `Dồn TOÀN BỘ số tháng 1 vào ${weekLabel(baseMonth, tuanDon)} · miền ${mienDon} (một ô, không chia cho miền khác) cho ${kq.dong.toLocaleString('vi-VN')} ${doiTuong}${phamVi}.\n\n`
        + `Số tuần/miền đang nhập của các SKU này sẽ bị thay: ${tenDon} nhận hết, mọi ô khác về 0${kq.oGhiDe ? ` (${kq.oGhiDe.toLocaleString('vi-VN')} ô đang có số sẽ đổi)` : ''}. `;
    if (!(await appConfirm(
      cachChia
      + (kq.boQua ? `${kq.boQua.toLocaleString('vi-VN')} SKU lệch nhưng có số tháng 1 bằng 0 sẽ KHÔNG bị đụng tới. ` : '')
      + 'Chưa lưu cho tới khi bấm "Lưu bản thảo".',
      { title: tatCa ? (tuanDon === undefined ? 'Rải đều TẤT CẢ các dòng?' : `Dồn TẤT CẢ các dòng vào ${tenDon}?`) : (tuanDon === undefined ? 'Rải đều tất cả dòng lệch?' : `Dồn tất cả dòng lệch vào ${tenDon}?`), okLabel: `${tuanDon === undefined ? 'Rải đều' : 'Dồn vào ' + tenDon} ${kq.dong.toLocaleString('vi-VN')} dòng`, danger: true }
    ))) return;
    handleCellsChange(kq.updates);
    setMessage({ type: 'success', text: `Đã ${tuanDon === undefined ? 'rải đều' : 'dồn vào ' + tenDon} ${kq.dong.toLocaleString('vi-VN')} SKU — bấm "Lưu bản thảo" để lưu.` });
  };

  const saveChanges = async () => {
    if (!selectedVersion) throw new Error('Chưa chọn bản cập nhật để lưu.');
    if (dirtyKeys.size === 0) return { skipped: true };

    const splits = [...dirtyKeys].map((key) => {
      const parts = key.split('_');
      const regionCode = parts.pop();
      const weekNumber = Number(parts.pop());
      return {
        skuCode: parts.join('_'),
        weekNumber,
        regionCode,
        quantity: weeklyMap[key] || 0
      };
    });

    const res = await api.saveWeeklySplits(selectedVersion.id, splits);
    setDirtyKeys(new Set());
    daLuuRef.current = { ...weeklyMap };
    setLichSu([]);
    return res;
  };

  const handleSave = async () => {
    if (choDuyet && dirtyKeys.size > 0
      && !(await appConfirm('Bản đang chờ duyệt — lưu sẽ rút yêu cầu duyệt, cần gửi lại. Vẫn lưu?', {
        title: 'Lưu sẽ rút yêu cầu duyệt', okLabel: 'Lưu và rút yêu cầu duyệt'
      }))) return;
    setSaving(true);
    try {
      const res = await saveChanges();
      // Server vừa đưa chu kỳ về nháp: nạp lại cả màn để trạng thái đúng với server
      // (loadAll tính lại cả phần kiểm tra khớp số).
      if (res.approvalWithdrawn) await loadAll(selectedCycle.id, selectedVersion.id);
      else setValidationResult(await api.validateWeeklySplits(selectedVersion.id));
      setMessage({
        type: 'success',
        text: res.skipped ? 'Không có thay đổi nào để lưu.' : res.message
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCycle || !selectedVersion) return;
    // Gửi duyệt hỏi lại, nói rõ gửi cái gì (đơn vị, chu kỳ, bản, tổng) — xem MonthlyForecast.
    const nhanBan = selectedVersion.iso_week_label || (selectedVersion.update_week ? `Tuần ${selectedVersion.update_week}` : 'bản đang chọn');
    if (!(await appConfirm(
      `Kiểm tra và gửi kế hoạch Forecast của đơn vị ${currentBU} — chu kỳ ${monthLabelFull(selectedCycle.base_month)}, bản ${nhanBan} — để thẩm định?\n\n`
      + `Bảng tuần/miền: tổng ${tongCuoi.tuan.toLocaleString('vi-VN')} chiếc so với FC tháng 1 là ${tongCuoi.thang.toLocaleString('vi-VN')} chiếc.`
      + (dirtyKeys.size ? ` ${dirtyKeys.size} ô chưa lưu sẽ được lưu trước khi gửi.` : '')
      + (choDuyet ? ' Bản đang chờ duyệt: gửi lại sẽ thay yêu cầu cũ.' : ''),
      { title: 'Kiểm tra và gửi phê duyệt?', okLabel: 'Gửi phê duyệt' }
    ))) return;
    setSaving(true);
    setMessage(null);
    try {
      await saveChanges();

      const valRes = await api.validateWeeklySplits(selectedVersion.id);
      setValidationResult(valRes);
      if (!valRes.isValid) {
        setMessage({
          type: 'error',
          text: `Không thể gửi duyệt: còn ${valRes.mismatchesCount} SKU có tổng tuần/miền chưa khớp kế hoạch tháng 1.`
        });
        return;
      }

      const res = await api.submitCycle(selectedCycle.id, selectedVersion.id);
      setMessage({ type: 'success', text: res.message });
      await loadAll(selectedCycle.id, selectedVersion.id);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  /** Bật lại bộ lọc thì chốt lại danh sách theo số hiện tại (kể cả số chưa lưu). */
  const handleToggleNonZero = (checked) => {
    setOnlyNonZero(checked);
    if (checked) setNonZeroSkus(computeNonZero(monthlyMap, weeklyMap));
  };

  const handleToggleLech = (checked) => {
    setChiLech(checked);
    if (checked) setLechSkus(tapSkuLech(tatCa, monthlyMap, weeklyMap, weeks, regionCodes));
  };

  const getSkuWeeklySum = (skuCode) =>
    weeks.reduce((sum, w) => sum + regionCodes.reduce(
      (s, r) => s + (weeklyMap[`${skuCode}_${w}_${r}`] || 0), 0
    ), 0);

  /**
   * Chốt danh sách hiển thị theo từng lần TẢI dữ liệu, không tính lại theo
   * từng phím gõ. Bản cũ duyệt 750 SKU x số tuần x số miền cho MỖI lần
   * render, và vì đọc thẳng weeklyMap đang sửa nên xoá ô cuối cùng của một
   * dòng là dòng đó biến mất ngay dưới con trỏ.
   */
  // Danh mục đang bật + SKU ngoài danh mục có số (dòng riêng, có nhãn). Mã vừa được thêm vào danh mục thì không lặp.
  const tatCa = useMemo(() => {
    const co = new Set(products.map((p) => String(p.sku_code).trim()));
    return products.concat(skuNgoai.filter((p) => !co.has(String(p.sku_code).trim())));
  }, [products, skuNgoai]);

  const filteredProducts = useMemo(() => {
    const s = search.trim().toLowerCase();
    return tatCa.filter((p) => {
      const matchSearch = !s
        || String(p.sku_code).toLowerCase().includes(s)
        || String(p.name).toLowerCase().includes(s);
      // String() hai ben: nonZeroSkus dung tu khoa cua forecastMap nen luon la
      // CHUOI, con p.sku_code co the la SO — Sheets bien "2013050022" thanh so
      // ngay khi mot lenh setValues ghi lai o do. Set.has so theo kieu, nen
      // thieu String() la KHONG mot ma nao khop va bo loc quet sach bang.
      const matchNonZero = !onlyNonZero || nonZeroSkus.has(String(p.sku_code));
      const matchLech = !chiLech || lechSkus.has(String(p.sku_code));
      return matchSearch && matchNonZero && matchLech;
    });
  }, [tatCa, search, onlyNonZero, nonZeroSkus, chiLech, lechSkus]);

  // Số dòng đang lệch NGAY LÚC NÀY (chỉ để hiện con số trên nút — danh sách lọc dùng bản đã chốt ở trên).
  const soDongLech = useMemo(
    () => tatCa.filter((p) => laDongLech(p.sku_code, monthlyMap, weeklyMap, weeks, regionCodes)).length,
    [tatCa, monthlyMap, weeklyMap, weeks, regionCodes]
  );

  const columnCount = 3 + weeks.length * regionCodes.length + 2;

  // Nguyên nhân từng SKU trong cảnh báo lệch: ngoài danh mục (dòng riêng, có nhãn) / có số ở ô không hiện trên bảng.
  const ghiChuLech = useMemo(() => {
    const ra = {};
    const ngoaiSet = new Set(skuNgoai.map((p) => String(p.sku_code).trim()));
    const anTheoSku = {};
    oAn.forEach((o) => { (anTheoSku[o.sku] = anTheoSku[o.sku] || []).push(o); });
    ((validationResult && validationResult.mismatches) || []).forEach((m) => {
      const ma = String(m.sku_code).trim();
      const phan = [];
      if (ngoaiSet.has(ma)) phan.push(`SKU không nằm trong danh mục đang bật của ${currentBU} (đã ngừng / kênh khác / chưa có trong danh mục) — đã hiện thành dòng riêng ở bảng dưới, gắn nhãn "ngoài danh mục"`);
      if (anTheoSku[ma]) {
        const tong = anTheoSku[ma].reduce((a, o) => a + o.sl, 0);
        phan.push(`${tong.toLocaleString('vi-VN')} chiếc nằm ở ô tuần/miền không hiện trên bảng (${anTheoSku[ma].slice(0, 3).map((o) => `T${o.tuan}·${o.mien}`).join(', ')}${anTheoSku[ma].length > 3 ? '…' : ''}) — dùng nút "Xoá số ở ô không hiển thị"`);
      }
      if (phan.length) ra[ma] = phan.join('; ');
    });
    return ra;
  }, [validationResult, skuNgoai, oAn, currentBU]);

  /** "Cho vào chung kênh": Kênh mặc định = trống cho các SKU ngoài danh mục (hàng dự báo ở nhiều kênh) -> hiện trong danh mục của mọi đơn vị. Chỉ quản trị viên trung tâm. */
  const choVaoChungKenh = async () => {
    const ds = skuNgoai.map((p) => String(p.sku_code).trim());
    if (!ds.length) return;
    if (!(await appConfirm(
      `Cho ${ds.length.toLocaleString('vi-VN')} SKU (${ds.slice(0, 6).join(', ')}${ds.length > 6 ? '…' : ''}) vào CHUNG KÊNH?\n\n`
      + 'Kênh mặc định của các mã sẽ để trống: mã hiện trong danh mục của MỌI đơn vị, có thể dự báo ở nhiều kênh, và hết nhãn "ngoài danh mục". Không đổi số liệu nào.',
      { title: 'Cho vào chung kênh?', okLabel: `Cho ${ds.length.toLocaleString('vi-VN')} SKU vào chung kênh` }
    ))) return;
    setLoading(true);
    try {
      const kq = await datKenhChungChoCacMa(ds, (sp) => api.updateProduct(sp));
      setMessage(kq.loi.length
        ? { type: 'error', text: `Đã cho ${kq.xong.length} SKU vào chung kênh; ${kq.loi.length} SKU chưa được: ${kq.loi.slice(0, 3).map((x) => x.ma + ' (' + x.ly + ')').join('; ')}.` }
        : { type: 'success', text: `Đã cho ${kq.xong.length} SKU vào chung kênh — các mã hiện trong danh mục của mọi đơn vị.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    await loadAll(selectedCycle?.id, selectedVersion?.id);
  };

  /** Đưa về 0 mọi ô đang có số nhưng nằm ở tuần/miền không hiện trên bảng (số cũ còn sót, ví dụ tuần 5 của tháng chỉ có 4 tuần). Chưa lưu cho tới khi bấm "Lưu bản thảo". */
  const xoaOAn = async () => {
    if (!oAn.length) return;
    const tong = oAn.reduce((a, o) => a + o.sl, 0);
    if (!(await appConfirm(
      `Đưa về 0 ${oAn.length.toLocaleString('vi-VN')} ô đang có tổng ${tong.toLocaleString('vi-VN')} chiếc ở tuần/miền KHÔNG hiện trên bảng này.\n\nChưa lưu cho tới khi bấm "Lưu bản thảo"; có thể "Bỏ thay đổi chưa lưu" để quay lại.`,
      { title: 'Xoá số ở ô không hiển thị?', okLabel: `Xoá ${oAn.length.toLocaleString('vi-VN')} ô`, danger: true }
    ))) return;
    const buoc = oAn.map((o) => ({ key: o.khoa, cu: o.sl }));
    setLichSu((h) => [...h.slice(-29), buoc]);
    setWeeklyMap((prev) => { const next = { ...prev }; oAn.forEach((o) => { next[o.khoa] = 0; }); return next; });
    setDirtyKeys((prev) => { const next = new Set(prev); oAn.forEach((o) => next.add(o.khoa)); return next; });
    setOAn([]);
    setMessage({ type: 'success', text: `Đã đưa về 0 ${oAn.length.toLocaleString('vi-VN')} ô không hiển thị — bấm "Lưu bản thảo" để lưu.` });
  };

  // Sắp xếp theo cột. Thứ tự CHỐT lúc bấm tiêu đề (xem useTableSort): gõ số không làm dòng nhảy chỗ,
  // và sắp xếp không đụng tới dữ liệu đang sửa — chỉ đảo thứ tự hiển thị.
  const cotLuoi = {
    sku: { type: 'text', get: (p) => p.sku_code },
    ten: { type: 'text', get: (p) => p.name },
    fc: { type: 'number', get: (p) => monthlyMap[p.sku_code] || 0 },
    tuan: { type: 'number', get: (p) => getSkuWeeklySum(p.sku_code) },
    lech: { type: 'number', get: (p) => getSkuWeeklySum(p.sku_code) - (monthlyMap[p.sku_code] || 0) }
  };
  const { rows: sapXep, spec: sortLuoi, toggle: doiSapXep } = useTableSort(filteredProducts, cotLuoi, maSku);

  // Dòng tổng ở chân bảng: FC tháng 1, từng ô tuần/miền, tổng tuần, lệch — cộng trên TOÀN BỘ danh mục
  // (không theo ô tìm kiếm / ô "chỉ hiện SKU có số lượng"), cùng quy ước với Bảng 0.
  const tongCuoi = useMemo(() => {
    const cot = {};
    let thang = 0;
    let tuan = 0;
    tatCa.forEach((p) => {
      thang += monthlyMap[p.sku_code] || 0;
      weeks.forEach((w) => regionCodes.forEach((r) => {
        const q = weeklyMap[`${p.sku_code}_${w}_${r}`] || 0;
        if (!q) return;
        cot[`${w}_${r}`] = (cot[`${w}_${r}`] || 0) + q;
        tuan += q;
      }));
    });
    return { cot, thang, tuan };
  }, [tatCa, monthlyMap, weeklyMap, weeks, regionCodes]);

  const scrollParentRef = useRef(null);
  // Chiều cao dòng ước lượng theo mật độ bảng (Cài đặt → Giao diện): Gọn thấp hơn 8px (utils/density.js).
  const chieuCaoDong = useRowHeight(ROW_HEIGHT_PX);
  const rowVirtualizer = useVirtualizer({
    count: sapXep.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => chieuCaoDong,
    overscan: 12
  });
  useEffect(() => { rowVirtualizer.measure(); }, [chieuCaoDong, rowVirtualizer]);
  const virtualRows = rowVirtualizer.getVirtualItems();
  const topPad = virtualRows.length ? virtualRows[0].start : 0;
  const bottomPad = virtualRows.length ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end : 0;

  // Cột = từng tổ hợp (tuần, miền) theo đúng thứ tự hiển thị trái→phải
  const weeklyColumns = weeks.flatMap((w) => regionCodes.map((r) => ({ week: w, region: r })));
  const grid = useGridEditing({
    columns: weeklyColumns,
    rows: sapXep,
    getRowKey: (p) => p.sku_code,
    buildCellId: (sku, col) => `${sku}_${col.week}_${col.region}`,
    getCellValue: (sku, col) => weeklyMap[`${sku}_${col.week}_${col.region}`] || 0,
    onCellsChange: handleCellsChange,
    scrollToRow: (idx) => rowVirtualizer.scrollToIndex(idx, { align: 'auto' }),
    // "Điền xuống cả cột" chỉ chạm các dòng ĐANG HIỂN THỊ; có số cũ bị đè thì hỏi trước.
    xacNhanDienCot: async (updates, colIdx, value) => {
      const col = weeklyColumns[colIdx];
      const dem = demGhiDe(updates, (sku, c) => weeklyMap[`${sku}_${c.week}_${c.region}`] || 0);
      const cauHoi = cauHoiGhiDe(dem, `Điền ${Number(value).toLocaleString('vi-VN')} (số của dòng đầu) xuống ${updates.length.toLocaleString('vi-VN')} dòng đang hiển thị của cột ${weekLabel(baseMonth, col.week)} · ${col.region}.`);
      if (!cauHoi) return true;
      return appConfirm(cauHoi, { title: 'Điền xuống cả cột?', okLabel: `Ghi đè ${dem.ghiDe.toLocaleString('vi-VN')} ô`, danger: true });
    }
  });

  return (
    <div className="space-y-4">

      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">{MAN_HINH.weekly.tieuDe} <span className="text-sm font-semibold text-slate-500" title={MAN_HINH.weekly.mota}>(Bảng 1)</span></h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Đơn vị: <strong className="text-slate-800">{currentBU}</strong>
            {baseMonth && <> · Tháng chia tuần: <strong className="text-slate-800">{monthLabel(baseMonth)}</strong> ({weeks.length} tuần)</>}
            {dirtyKeys.size > 0 && (
              <span className="ml-2 text-amber-700 font-semibold">• {dirtyKeys.size} ô chưa lưu</span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Một nút chính theo "bước tiếp theo": còn ô chưa lưu thì Lưu là nút chính, hết thì Kiểm tra & gửi duyệt. */}
          <button
            onClick={handleSave}
            disabled={saving || !canWrite || dirtyKeys.size === 0}
            className={dirtyKeys.size > 0 ? NUT_CHINH : NUT_PHU}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{saving ? 'Đang lưu...' : 'Lưu bản thảo'}</span>
          </button>

          <button
            onClick={handleSubmit}
            disabled={saving || !canWrite}
            className={dirtyKeys.size > 0 ? NUT_PHU : NUT_CHINH}
          >
            <Send className="w-4 h-4" />
            <span>Kiểm tra &amp; gửi duyệt</span>
          </button>
        </div>
      </div>

      <CycleBar
        currentBU={currentBU}
        cycles={cycles}
        selectedCycle={selectedCycle}
        onSelectCycle={handleSelectCycle}
        versions={versions}
        selectedVersion={selectedVersion}
        onSelectVersion={handleSelectVersion}
        canEdit={isEditor}
        canReopen={canReopen}
        onChanged={(cycleId, versionId) => loadAll(cycleId, versionId)}
      />

      <ValidationAlert validationResult={validationResult} ghiChu={ghiChuLech} onChon={(ma) => { setSearch(ma); setOnlyNonZero(false); setChiLech(false); }} />

      {(skuNgoai.length > 0 || oAn.length > 0) && (
        <div className="bg-amber-50 border border-amber-300 text-amber-900 text-xs rounded-lg p-3 space-y-1">
          {skuNgoai.length > 0 && (
            <div>
              <strong>{skuNgoai.length.toLocaleString('vi-VN')} SKU có số nhưng không nằm trong danh mục đang bật của {currentBU}</strong>
              {' '}({skuNgoai.slice(0, 6).map((p) => p.sku_code).join(', ')}{skuNgoai.length > 6 ? '…' : ''}) — hiện ở bảng dưới thành dòng gắn nhãn "ngoài danh mục". Server vẫn cộng các số này khi kiểm khớp,
              nên hoặc sửa số tuần/miền cho khớp tháng 1, hoặc đưa về 0 nếu là số nhầm kênh / mã đã ngừng.
              {' '}Nếu mã được dự báo ở nhiều kênh thì cho vào <strong>chung kênh</strong>
              {duocDatKenhChung(user)
                ? <> — <button type="button" onClick={choVaoChungKenh} className="underline font-semibold hover:text-amber-950">Cho {skuNgoai.length} mã này vào chung kênh</button></>
                : ' (nhờ quản trị viên trung tâm: Danh mục SKU → Sửa → Kênh mặc định = Chung)'}.
            </div>
          )}
          {oAn.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span>
                <strong>{oAn.length.toLocaleString('vi-VN')} ô có số ở tuần/miền không hiện trên bảng này</strong>
                {' '}(tổng {oAn.reduce((a, o) => a + o.sl, 0).toLocaleString('vi-VN')} chiếc, ví dụ {oAn.slice(0, 3).map((o) => `${o.sku} T${o.tuan}·${o.mien}`).join(', ')}) — vẫn bị tính vào kiểm khớp.
              </span>
              {canWrite && (
                <button type="button" onClick={xoaOAn} className="border border-amber-400 bg-white hover:bg-amber-100 text-amber-900 px-2.5 py-1 rounded-lg font-semibold whitespace-nowrap">
                  Xoá số ở ô không hiển thị
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {choDuyet && isEditor && (
        <div className="bg-amber-50 border border-amber-300 text-amber-900 text-xs rounded-lg p-3">
          Bản đang chờ duyệt — lưu thay đổi sẽ rút yêu cầu duyệt, cần gửi lại.
        </div>
      )}

      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input
            type="text"
            placeholder="Tìm theo mã SKU hoặc tên sản phẩm..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs outline-none focus:border-blue-500"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
          <input type="checkbox" checked={onlyNonZero} onChange={(e) => handleToggleNonZero(e.target.checked)} />
          Chỉ hiện SKU có số lượng
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap" title="Chỉ hiện SKU có tổng tuần/miền khác FC tháng 1 (cột Lệch khác ✓)">
          <input type="checkbox" checked={chiLech} onChange={(e) => handleToggleLech(e.target.checked)} />
          Chỉ dòng lệch ({soDongLech.toLocaleString('vi-VN')})
        </label>
        {canWrite && (
          <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap" title="Cách phân bổ số tháng 1 vào bảng tuần khi soạn tay (nút đũa phép từng dòng và nút bên cạnh dùng chung lựa chọn này)">
            Phân bổ tháng đầu:
            <select
              value={tuanDon === undefined ? 'deu' : `w${tuanDon}`}
              onChange={(e) => setCheDoRai(e.target.value)}
              aria-label="Cách phân bổ số tháng 1 vào bảng tuần"
              className="border border-slate-300 rounded-lg bg-white px-2 py-1.5 text-xs text-slate-700"
            >
              <option value="deu">Rải đều các tuần × miền</option>
              {weeks.map((w) => <option key={w} value={`w${w}`}>Dồn hết vào {weekLabel(baseMonth, w)}</option>)}
            </select>
          </label>
        )}
        {canWrite && tuanDon !== undefined && (
          <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap" title="Miền nhận toàn bộ số tháng 1 khi dồn vào một tuần (mặc định MB)">
            Miền:
            <select
              value={mienDon}
              onChange={(e) => setMienDonChon(e.target.value)}
              aria-label="Miền nhận số khi dồn vào một tuần"
              className="border border-slate-300 rounded-lg bg-white px-2 py-1.5 text-xs text-slate-700"
            >
              {regionCodes.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        )}
        {canWrite && (
          <>
            <button
              type="button"
              onClick={hoanTac}
              disabled={lichSu.length === 0}
              title="Hoàn tác bước gần nhất (dán, điền xuống, phân bổ). Gõ tay từng ô không tính — dùng 'Bỏ thay đổi chưa lưu'."
              className="flex items-center gap-1.5 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap"
            >
              <Undo2 className="w-3.5 h-3.5" aria-hidden="true" />
              Hoàn tác{lichSu.length > 0 ? ` (${lichSu.length})` : ''}
            </button>
            <button
              type="button"
              onClick={boThayDoi}
              disabled={dirtyKeys.size === 0}
              title="Bỏ mọi ô đang sửa chưa lưu, quay về số đã lưu gần nhất"
              className="flex items-center gap-1.5 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap"
            >
              <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
              Bỏ thay đổi chưa lưu
            </button>
          </>
        )}
        {canWrite && (
          <button
            type="button"
            onClick={() => raiDeuTatCaLech(false)}
            disabled={soDongLech === 0}
            title={tuanDon === undefined
              ? 'Chia đều số tháng 1 ra các ô tuần × miền cho mọi SKU đang lệch (có xem trước và hỏi xác nhận)'
              : `Dồn toàn bộ số tháng 1 vào ${tenDon} cho mọi SKU đang lệch (có xem trước và hỏi xác nhận)`}
            className="flex items-center gap-1.5 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap"
          >
            <ListChecks className="w-3.5 h-3.5" aria-hidden="true" />
            {tuanDon === undefined ? 'Rải đều tất cả dòng lệch' : `Dồn tất cả dòng lệch vào ${tenDon}`}
          </button>
        )}
        {canWrite && (
          <button
            type="button"
            onClick={() => raiDeuTatCaLech(true)}
            disabled={Object.keys(monthlyMap).every((k) => !(monthlyMap[k] > 0))}
            title={(tuanDon === undefined
              ? 'Rải đều số tháng 1 cho MỌI SKU có số tháng 1, kể cả dòng đang khớp — dùng khi bản thảo phân bổ sai cách và cần làm lại'
              : `Dồn toàn bộ số tháng 1 vào ${tenDon} cho MỌI SKU có số tháng 1, kể cả dòng đang khớp — dùng khi bản thảo phân bổ sai cách và cần làm lại`)
              + ' (có xem trước và hỏi xác nhận; chỉ các dòng đang hiển thị)'}
            className="flex items-center gap-1.5 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap"
          >
            <ListChecks className="w-3.5 h-3.5" aria-hidden="true" />
            {tuanDon === undefined ? 'Rải đều tất cả các dòng' : `Dồn tất cả các dòng vào ${tenDon}`}
          </button>
        )}
        {isEditor && (
          <>
            <button
              onClick={() => setShowImport(true)}
              disabled={!canWrite}
              className="flex items-center gap-1.5 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              Nhập từ file
            </button>
            {/* Thao tác ít dùng gom vào "⋯ Thêm" để màn chỉ còn một nút chính. */}
            <MoreMenu items={[
              { label: 'Thêm SKU vào danh mục', icon: PackagePlus, onClick: () => setShowAddProduct(true) }
            ]} />
          </>
        )}
      </div>

      {showImport && (
        <React.Suspense fallback={null}>
          <ImportForecastModal
            currentBU={currentBU}
            groups={groups}
            bus={bus}
            monthColumns={monthsOfCycle(selectedCycle)}
            monthColumnLabel={monthLabel}
            weekColumns={weeks}
            weekBaseMonthLabel={baseMonth ? monthLabel(baseMonth) : ''}
            regionCodes={regionCodes}
            versionId={selectedVersion?.id}
            unsavedCount={dirtyKeys.size}
            cycleStatus={selectedCycle?.status}
            onClose={() => setShowImport(false)}
            onProductsAdded={(newProducts) => {
              setProducts((prev) => [...prev, ...newProducts]);
            }}
            onImported={async ({ monthlyUpdates, weeklyUpdates }) => {
              const parts = [];
              let daRut = false;
              if (monthlyUpdates.length) {
                const lines = monthlyUpdates.map(({ rowKey, col, value }) => ({
                  skuCode: rowKey, forecastMonth: col, quantity: value
                }));
                // Nhập lại = ghi đè trọn bản kế hoạch này: SKU không còn trong file
                // phải biến mất, không được nằm lại cộng vào tổng.
                const r = await api.saveMonthlyLines(selectedVersion.id, lines, true);
                daRut = daRut || !!r.approvalWithdrawn;
                parts.push(`${lines.length} ô Bảng tháng`);
              }
              if (weeklyUpdates.length) {
                const splits = weeklyUpdates.map(({ rowKey, col, value }) => ({
                  skuCode: rowKey, weekNumber: col.week, regionCode: col.region, quantity: value
                }));
                const r = await api.saveWeeklySplits(selectedVersion.id, splits, true);
                daRut = daRut || !!r.approvalWithdrawn;
                parts.push(`${splits.length} ô Bảng tuần/miền`);
              }
              // Rút duyệt -> trạng thái chu kỳ đổi, nạp lại cả màn.
              if (daRut) await loadAll(selectedCycle.id, selectedVersion.id);
              else await loadForecasts(selectedVersion.id, normalizeMonth(selectedCycle.base_month));
              setMessage({
                type: 'success',
                text: (parts.length ? `Đã lưu ${parts.join(' và ')}.` : 'Không có ô nào được cập nhật.')
                  + (daRut ? ' Bản đang chờ duyệt đã được rút về nháp — cần gửi duyệt lại.' : '')
              });
            }}
          />
        </React.Suspense>
      )}

      {showAddProduct && (
        <AddProductModal
          groups={groups}
          bus={bus}
          defaultChannel={currentBU}
          onClose={() => setShowAddProduct(false)}
          onAdded={(product) => {
            setProducts((prev) => [...prev, product]);
            setShowAddProduct(false);
            setMessage({ type: 'success', text: `Đã thêm SKU ${product.sku_code} vào danh mục.` });
          }}
        />
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div ref={scrollParentRef} className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-800 text-white font-semibold sticky top-0 z-20">
              <tr>
                <SortTh rowSpan="2" label="Mã SKU" sortKey="sku" spec={sortLuoi} onSort={doiSapXep} className="sticky left-0 z-30 bg-slate-800 shadow-[inset_-1px_0_0_#475569] py-2 px-3 border-r border-slate-700 w-28 min-w-28" />
                <SortTh rowSpan="2" label="Tên sản phẩm" sortKey="ten" spec={sortLuoi} onSort={doiSapXep} className="py-2 px-3 border-r border-slate-700 min-w-[180px]" />
                <SortTh rowSpan="2" label="FC tháng 1" sortKey="fc" spec={sortLuoi} onSort={doiSapXep} className="py-2 px-3 border-r border-slate-700 text-right w-24 bg-blue-900/60" />
                {weeks.map((w) => (
                  <th
                    key={w}
                    colSpan={regionCodes.length}
                    className="py-2 px-3 border-r border-slate-700 text-center bg-slate-900/60 whitespace-nowrap"
                  >
                    {weekLabel(baseMonth, w)}
                  </th>
                ))}
                <SortTh rowSpan="2" label="Tổng tuần" sortKey="tuan" spec={sortLuoi} onSort={doiSapXep} className="py-2 px-3 border-r border-slate-700 text-right w-24 bg-cyan-900/60" />
                <SortTh rowSpan="2" label="Lệch" sortKey="lech" spec={sortLuoi} onSort={doiSapXep} className="py-2 px-3 text-right w-24" />
              </tr>
              <tr>
                {weeklyColumns.map((col, colIdx) => (
                  <th key={`${col.week}-${col.region}`} className="py-1.5 px-2 border-r border-slate-700 text-right text-[10px] font-mono bg-slate-900/40">
                    <div className="flex items-center justify-end gap-1">
                      {col.region}
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => grid.fillColumnDown(colIdx)}
                          title="Điền giá trị dòng đầu xuống toàn bộ cột này"
                          className="p-0.5 rounded hover:bg-slate-700"
                        >
                          <ArrowDownToLine className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 font-mono">
              {loading ? (
                <StateRow colSpan={columnCount} kind="loading" />
              ) : loiTai ? (
                <StateRow colSpan={columnCount} kind="error" text={loiTai} onRetry={() => loadAll(selectedCycle?.id, selectedVersion?.id)} />
              ) : !selectedVersion ? (
                <StateRow colSpan={columnCount} kind="empty" text="Chưa có chu kỳ nào cho đơn vị này. Mở chu kỳ ở thanh phía trên trước." />
              ) : filteredProducts.length === 0 ? (
                <StateRow colSpan={columnCount} kind="empty" text="Không tìm thấy SKU phù hợp" />
              ) : (
                <>
                  {topPad > 0 && <tr style={{ height: topPad }} aria-hidden="true" />}
                  {virtualRows.map((vRow) => {
                  const p = sapXep[vRow.index];
                  const monthQty = monthlyMap[p.sku_code] || 0;
                  const weekSum = getSkuWeeklySum(p.sku_code);
                  const diff = weekSum - monthQty;

                  return (
                    <tr key={p.sku_code} className="hover:bg-blue-50/50 transition">
                      <td className="sticky left-0 z-10 bg-white shadow-[inset_-1px_0_0_#e2e8f0] py-2 px-3 border-r border-slate-200 font-bold text-slate-800">
                        {p.sku_code}
                        {p._ngoai && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold font-sans align-middle" title={`SKU này có số nhưng không nằm trong danh mục đang bật của ${currentBU} (đã ngừng / kênh khác / chưa có trong danh mục). Hiện ở đây để sửa được số tuần/miền.`}>ngoài danh mục</span>}
                      </td>
                      <td className="py-2 px-3 border-r border-slate-200 font-sans font-medium text-slate-900 truncate max-w-xs">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{p.name}</span>
                          {canWrite && monthQty > 0 && (
                            <button
                              onClick={() => distributeEvenly(p.sku_code)}
                              title={tuanDon === undefined ? 'Rải đều số tháng 1 ra các ô tuần/miền' : `Dồn toàn bộ số tháng 1 vào ${tenDon}`}
                              className="flex-shrink-0 p-1 rounded hover:bg-blue-100 text-blue-600"
                            >
                              <Wand2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 border-r border-slate-200 text-right font-bold text-blue-800 bg-blue-50/40">
                        {monthQty.toLocaleString('vi-VN')}
                      </td>

                      {weeklyColumns.map((col, colIdx) => {
                        const key = `${p.sku_code}_${col.week}_${col.region}`;
                        const isDirty = dirtyKeys.has(key);
                        return (
                          <td key={key} className="p-1 border-r border-slate-200 text-right">
                            <input
                              ref={grid.registerRef(key)}
                              type="number"
                              min="0"
                              step="1"
                              disabled={!canWrite}
                              value={weeklyMap[key] ?? 0}
                              {...CHAN_LAN_CHUOT}
                              onChange={(e) => handleCellChange(p.sku_code, col.week, col.region, e.target.value)}
                              onKeyDown={(e) => canWrite && grid.handleKeyDown(e, vRow.index, colIdx)}
                              onPaste={(e) => canWrite && grid.handlePaste(e, vRow.index, colIdx)}
                              className={`w-16 text-right px-1.5 py-1 rounded font-semibold outline-none transition disabled:text-slate-500 disabled:cursor-not-allowed ${
                                isDirty
                                  ? 'bg-amber-50 ring-1 ring-amber-300 text-amber-900'
                                  : 'bg-transparent hover:bg-white focus:bg-white focus:ring-2 focus:ring-blue-500 text-slate-900'
                              }`}
                            />
                          </td>
                        );
                      })}

                      <td className="py-2 px-3 border-r border-slate-200 text-right font-bold text-slate-800 bg-slate-50">
                        {weekSum.toLocaleString('vi-VN')}
                      </td>
                      <td className={`py-2 px-3 text-right font-bold ${
                        diff === 0 ? 'text-emerald-700' : 'text-rose-700 bg-rose-50'
                      }`}>
                        {diff === 0 ? '✓' : diff > 0 ? `+${diff.toLocaleString('vi-VN')}` : diff.toLocaleString('vi-VN')}
                      </td>
                    </tr>
                  );
                  })}
                  {bottomPad > 0 && <tr style={{ height: bottomPad }} aria-hidden="true" />}
                </>
              )}
            </tbody>

            {!loading && !loiTai && selectedVersion && tatCa.length > 0 && (
              <tfoot className="bg-slate-100 font-bold border-t-2 border-slate-300 text-slate-900 sticky bottom-0 z-10">
                <tr>
                  <td className="sticky left-0 z-10 bg-slate-100 shadow-[inset_-1px_0_0_#cbd5e1] py-2.5 px-3 uppercase text-slate-700 text-xs">Tổng</td>
                  <td className="py-2.5 px-3 uppercase text-slate-700 text-right text-xs">
                    Tổng cộng (chiếc):
                    {(search.trim() !== '' || onlyNonZero) && (
                      <span className="ml-2 normal-case font-normal text-[11px] text-slate-600">cả danh mục — không theo bộ lọc đang bật</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-blue-900">{tongCuoi.thang.toLocaleString('vi-VN')}</td>
                  {weeklyColumns.map((col) => (
                    <td key={`${col.week}-${col.region}`} className="py-2.5 px-2 text-right font-mono text-[11px]">
                      {(tongCuoi.cot[`${col.week}_${col.region}`] || 0).toLocaleString('vi-VN')}
                    </td>
                  ))}
                  <td className="py-2.5 px-3 text-right font-mono text-cyan-900">{tongCuoi.tuan.toLocaleString('vi-VN')}</td>
                  <td className={`py-2.5 px-3 text-right font-mono ${tongCuoi.tuan === tongCuoi.thang ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {tongCuoi.tuan === tongCuoi.thang ? '✓' : (tongCuoi.tuan > tongCuoi.thang ? '+' : '') + (tongCuoi.tuan - tongCuoi.thang).toLocaleString('vi-VN')}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

    </div>
  );
}
